import {Component, computed, effect, inject, OnDestroy, OnInit, signal, TrackByFunction, untracked} from '@angular/core';
import {DatePipe, NgClass, NgTemplateOutlet} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Card} from 'primeng/card';
import {Tab, TabList, Tabs} from 'primeng/tabs';
import {Textarea} from 'primeng/textarea';
import {TableModule} from 'primeng/table';
import {DatePicker} from 'primeng/datepicker';
import {AutoComplete, AutoCompleteCompleteEvent} from 'primeng/autocomplete';
import {Select} from 'primeng/select';
import {Checkbox} from 'primeng/checkbox';
import {InputText} from 'primeng/inputtext';
import {RadioButton} from 'primeng/radiobutton';
import {FloatLabel} from 'primeng/floatlabel';
import {Tooltip} from 'primeng/tooltip';
import {
  BaseOption,
  ItineraryItemDetails,
  LocationCategory,
  LocationOption,
  UserTravelItinerary,
} from '../../../../api';
import {IconBtnComponent} from '../../icon-b/icon-btn.component';
import {CreateTemplateBaseComponent} from '../create-template-base-component.directive';
import {itineraryForTraveller, primaryTravellerId, sortedItems} from '../../../../datasources/template.datasource';
import {TaSubmissionDatasource} from '../../../../datasources/ta-submission.datasource';
import {LocationService} from '../../../../services/location.service';
import {parseTypedTime, toLocalISOString} from '../../../../util/date.utils';
import {fuzzySearch} from '../../../../util/search.utils';
import {enabledMealTypes} from '../../../../util/itinerary.utils';
import {MealTypes} from '../../../../util/app.types';

interface TemplateItineraryRow extends ItineraryItemDetails {
  dayNumber: number;
  startDate?: Date;
  endDate?: Date;
  startLocation?: LocationOption;
  endLocation?: LocationOption;
  isOverseas: boolean;
  supportingInfoLabel: string;
  // MealTypes the server's rules enabled for this day.
  allowedFields: string[];
}

/**
 * Template version of create-ta-itinerary. Days are numbered rather than dated. The TA meal rules
 * run server-side on every save, so claims are gated by work type as on a TA; there is no manual
 * override.
 */
@Component({
  selector: 'create-template-itinerary',
  imports: [
    IconBtnComponent,
    Card,
    Tab,
    TabList,
    Tabs,
    FormsModule,
    Textarea,
    TableModule,
    DatePicker,
    AutoComplete,
    Select,
    Checkbox,
    InputText,
    RadioButton,
    FloatLabel,
    Tooltip,
    DatePipe,
    NgClass,
    NgTemplateOutlet,
  ],
  templateUrl: './create-template-itinerary.html',
})
export class CreateTemplateItinerary extends CreateTemplateBaseComponent implements OnInit, OnDestroy {
  private locationService = inject(LocationService);
  private taSubmissionDatasource = inject(TaSubmissionDatasource);

  selectedTravellerId = signal<string>('');
  primaryTravellerId = computed(() => primaryTravellerId(this.template()));

  // Editable: the datasource's itinerary for the selected tab. Read-only (review): looked up in the
  // template's own list, so viewing never creates an itinerary.
  itinerary = computed<UserTravelItinerary | undefined>(() => this.isReadOnly()
    ? itineraryForTraveller(this.template(), this.selectedTravellerId())
    : this.templateDatasource.currentItinerary());

  rows = computed<TemplateItineraryRow[]>(() => sortedItems(this.itinerary()).map((item, index) => {
    const startLocation = this.taSubmissionDatasource.findLocation(item.startLocationId ?? '', item.startLocationNameId ?? '');
    const endLocation = this.taSubmissionDatasource.findLocation(item.endLocationId ?? '', item.endLocationNameId ?? '');
    return {
      ...item,
      dayNumber: index + 1,
      startDate: item.start ? new Date(item.start) : undefined,
      endDate: item.end ? new Date(item.end) : undefined,
      startLocation,
      endLocation,
      // Overseas when either end is overseas
      isOverseas: startLocation?.category === LocationCategory.Overseas || endLocation?.category === LocationCategory.Overseas,
      supportingInfoLabel: this.getSupportingInfoLabel(item.travelMode ?? ''),
      allowedFields: enabledMealTypes(item.enabledMeals),
    };
  }));

  // By server id so a save response does not rebuild the row holding an open control; see create-ta-itinerary.
  trackItineraryItem: TrackByFunction<TemplateItineraryRow> = (index, item) => item.id ?? index;

  enabledLocations = computed<LocationOption[]>(() =>
    this.store.availableLocations().filter((l: LocationOption) => !!l.enabled));
  filteredLocations = signal<LocationOption[]>([]);
  // Enabled locations this itinerary already uses, in itinerary order; offered first on a blank search.
  recentLocations = computed<LocationOption[]>(() => {
    const seen = new Set<string>();
    return this.rows()
      .flatMap(item => [item.startLocation, item.endLocation])
      .filter((location): location is LocationOption => {
        const key = `${location?.locationId}|${location?.nameId}`;
        if (!location?.enabled || seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
  });
  recentLocationCount = signal<number>(0);
  availableModesOfTravel: BaseOption[] = [];

  private stagedTime: { itemId: ItineraryItemDetails['id']; field: 'start' | 'end' } | null = null;

  // Separate from the itinerary so typing is not slowed by store updates; see create-ta-itinerary.
  workingNotes = signal<string>('');
  private notesDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private lastSyncedItineraryId: string | null = null;

  constructor() {
    super();
    effect(() => {
      const itinerary = this.itinerary();
      const id = itinerary?.id ?? null;
      if (id !== this.lastSyncedItineraryId) {
        this.lastSyncedItineraryId = id;
        untracked(() => this.workingNotes.set(itinerary?.notes ?? ''));
      }
    });

    // Seed the default suggestions once the location list lands, capped as in create-ta-itinerary.
    effect(() => {
      const locations = this.enabledLocations();
      untracked(() => {
        this.recentLocationCount.set(0);
        this.filteredLocations.set(locations.slice(0, 250));
      });
    });
  }

  ngOnInit(): void {
    this.availableModesOfTravel = this.store.session()?.details?.travelModeOptions ?? [];
    this.locationService.loadLocations();

    const travellerId = this.primaryTravellerId() ?? this.sortedTravellers().at(0)?.travellerId;
    if (travellerId) {
      this.selectTraveller(travellerId);
    }
  }

  selectTraveller(travellerId: string): void {
    this.selectedTravellerId.set(travellerId);
    if (!this.isReadOnly()) {
      this.templateDatasource.loadTravellerItinerary(travellerId);
    }
  }

  isLinkedToPrimary(travellerId: string | null | undefined): boolean {
    const primaryItinerary = itineraryForTraveller(this.template(), this.primaryTravellerId());
    return !!travellerId && !!primaryItinerary && primaryItinerary === itineraryForTraveller(this.template(), travellerId);
  }

  updateItineraryItemField(itineraryItem: TemplateItineraryRow, field: keyof ItineraryItemDetails, event: any): void {
    const item: ItineraryItemDetails = structuredClone(this.toItem(itineraryItem));

    if (field === 'startLocationId') {
      item.startLocationId = event.value.locationId;
      item.startLocationNameId = event.value.nameId;
    } else if (field === 'endLocationId') {
      item.endLocationId = event.value.locationId;
      item.endLocationNameId = event.value.nameId;
    } else if (field === 'mealClaims') {
      item.mealClaims = {...(item.mealClaims ?? {}), ...event};
    } else if (field === 'supportingInfo') {
      item.supportingInfo = (event.target as HTMLInputElement).value;
    } else if (field === 'start' || field === 'end' || field === 'isPersonalDay') {
      item[field] = event;
    } else if (field === 'cateringOption' || field === 'accommodationType' || field === 'travelMode') {
      item[field] = event.value;
    }
    this.templateDatasource.saveItineraryItem(item);
  }

  // Strips the display-only fields before sending.
  private toItem(row: TemplateItineraryRow): ItineraryItemDetails {
    const {dayNumber, startDate, endDate, startLocation, endLocation, isOverseas, supportingInfoLabel, allowedFields, ...item} = row;
    return item;
  }

  stageTime(itineraryItem: ItineraryItemDetails, field: 'start' | 'end'): void {
    this.stagedTime = {itemId: itineraryItem.id, field};
  }

  // As create-ta-itinerary: parse the typed text, round to 15 min, keep the day's date; revert when
  // unparseable or end <= start.
  commitTime(itineraryItem: TemplateItineraryRow, field: 'start' | 'end', picker: DatePicker): void {
    const staged = this.stagedTime;
    if (!staged || staged.itemId !== itineraryItem.id || staged.field !== field) {
      return;
    }
    this.stagedTime = null;

    const current = new Date(itineraryItem[field]!);
    const typed = parseTypedTime(picker.inputfieldViewChild?.nativeElement.value ?? '');
    if (!typed) {
      picker.writeValue(current);
      return;
    }
    const minutes = Math.min(Math.round((typed.hours * 60 + typed.minutes) / 15) * 15, 23 * 60 + 45);
    const next = new Date(current);
    next.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    const other = field === 'start' ? itineraryItem.end : itineraryItem.start;
    if (other && (field === 'start' ? next >= new Date(other) : next <= new Date(other))) {
      picker.writeValue(current);
      return;
    }
    picker.writeValue(next);
    if (next.getTime() !== current.getTime()) {
      this.updateItineraryItemField(itineraryItem, field, toLocalISOString(next));
    }
  }

  onNotesChange(value: string): void {
    this.workingNotes.set(value);
    if (this.notesDebounceTimer) {
      clearTimeout(this.notesDebounceTimer);
    }
    this.notesDebounceTimer = setTimeout(() => this.flushNotes(), 500);
  }

  onNotesBlur(): void {
    this.flushNotes();
  }

  private flushNotes(): void {
    if (this.notesDebounceTimer) {
      clearTimeout(this.notesDebounceTimer);
      this.notesDebounceTimer = null;
    }
    if (this.isReadOnly()) return;
    const pending = this.workingNotes();
    if (pending !== (this.itinerary()?.notes ?? '')) {
      this.templateDatasource.saveItineraryNotes(pending);
    }
  }

  ngOnDestroy(): void {
    this.flushNotes();
  }

  filterLocations(event: AutoCompleteCompleteEvent) {
    const searching = !!event.query.trim();
    const recents = searching ? [] : this.recentLocations();
    this.recentLocationCount.set(recents.length);
    this.filteredLocations.set([
      ...recents,
      // Top 10 matches while searching; otherwise the first 150 locations.
      ...fuzzySearch(this.enabledLocations(), event.query, 'name').slice(0, searching ? 10 : 150),
    ]);
  }

  // Select the whole name on focus so typing replaces it; see create-ta-itinerary.
  selectLocationText(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    setTimeout(() => {
      if (document.activeElement === input && input.value === value) {
        input.select();
      }
    });
  }

  addDay(): void {
    this.templateDatasource.addItineraryDay();
  }

  removeDay(itineraryItem: TemplateItineraryRow): void {
    this.templateDatasource.removeItineraryDay(this.toItem(itineraryItem));
  }

  protected readonly MealTypes = MealTypes;

  getSupportingInfoLabel(travelMode: string): string {
    if (travelMode.toLowerCase().includes('car')) {
      return 'Number plate...';
    } else if (travelMode === 'Air') {
      return 'Flight number...';
    }
    return 'Enter...';
  }
}
